import type { StandardsParams, StandardsQueryInput } from '../-types';

/**
 * The two fields the standards list maps into its `ORDER BY`.
 *
 * The route's `sort` querystring is a regex over exactly these, so anything
 * else fails schema validation and 400s the list.
 */
const SORTABLE_FIELDS = new Set(['name', 'type']);

/** The two columns the route will filter on. */
const FILTERABLE_FIELDS = new Set(['name', 'type']);

/** The route caps `q` at 255 characters. */
const SEARCH_MAX = 255;

/**
 * Turns table state into the endpoint's parameters.
 *
 * Single conversion point, as on the other management lists: the query key is
 * what this returns, so the route loader (reading the URL) and the page's hook
 * (reading `useTable`) only share a cache entry if both derive their params the
 * same way.
 *
 * `page` converts from the UI's 1-based count to the endpoint's 0-based one.
 * Unlike the other management lists this endpoint has a working `q`, so the
 * table's search box goes to the server rather than filtering in memory.
 *
 * `isDirectoryList` is always sent. The list is `standards UNION ALL
 * certifications` and neither table constrains its `type` column, so without
 * this the route returns rows whose type is null or outside the seven claim
 * types — rows this screen cannot meaningfully edit, since the sheet would
 * PUT them to `/api/admin/standards/:id`. The old dashboard sent it on every
 * request for the same reason.
 */
export function toStandardsParams(input: StandardsQueryInput): StandardsParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (FILTERABLE_FIELDS.has(key) && values && values.length > 0) {
      filter[key] = values.join(',');
    }
  }
  const hasFilter = Object.keys(filter).length > 0;

  // Dropped rather than forwarded: an unsupported field would 400 the list.
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  const q = (input.search ?? '').trim().slice(0, SEARCH_MAX);

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    isDirectoryList: true,
    ...(sort ? { sort } : {}),
    ...(q ? { q } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

export function buildStandardsQs(params: StandardsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  qs.set('isDirectoryList', String(params.isDirectoryList));
  if (params.sort) qs.set('sort', params.sort);
  if (params.q) qs.set('q', params.q);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

/**
 * `translations` can come back parsed, as a raw JSON string, or null — the
 * response schema allows all three. Same handling as facility processes.
 */
export function parseTranslations(
  value: unknown,
): Record<string, { name?: string; description?: string }> | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'object') {
    return value as Record<string, { name?: string; description?: string }>;
  }
  if (typeof value !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, { name?: string; description?: string }>)
      : undefined;
  } catch {
    // A malformed column should leave the form blank, not crash the sheet.
    return undefined;
  }
}
